import React, { createContext, useContext, useState, useEffect } from 'react';

 const API_BASE_URL =  process.env.REACT_APP_API_BASE_URL;
// const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ;
//const API_BASE_URL = "http://localhost:5000";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);

  useEffect(() => {
    checkAuthStatus();
  }, []); 

  // Check current authentication status
  async function checkAuthStatus() {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/current_user`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const user = await response.json();
        setCurrentUser(user);
        setPermissions(user?.permissions || []);
        console.log('✅ User authenticated:', user?.email);
      } else {
        setCurrentUser(null);
        setPermissions([]);
        console.log('❌ No authenticated user');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setCurrentUser(null);
      setPermissions([]);
    }
    setLoading(false);
  }

  // Google OAuth login
  function loginWithGoogle() {
    console.log('🔄 Initiating Google OAuth...');
    window.location.href = `${API_BASE_URL}/auth/google`;
  }

  // GitHub OAuth login (if available)
  function loginWithGitHub() {
    console.log('🔄 Initiating GitHub OAuth...');
    window.location.href = `${API_BASE_URL}/auth/github`;
  }

  // Email/Password signup
  async function signup(email, password, name) {
    try {
      console.log('🔄 Attempting signup for:', email);

      const response = await fetch(`${API_BASE_URL}/auth/signup`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json' 
        },
        credentials: 'include',
        body: JSON.stringify({ email, password, name })
      });

      // Handle different response types
      let data;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        // Handle non-JSON responses (like rate limiting)
        const text = await response.text();
        data = { message: text };
      }

      if (!response.ok) {
        // Handle rate limiting specifically
        if (response.status === 429) {
          throw new Error('Too many requests. Please wait a moment before trying again.');
        }
        throw new Error(data.message || 'Signup failed');
      }

      console.log('✅ Signup successful:', data.user.email);
      setCurrentUser(data.user);
      setPermissions(data.user.permissions || []);

      // Store JWT token if provided
      if (data.token) {
        localStorage.setItem('auth_token', data.token);
      }

      return data.user;
    } catch (error) {
      console.error('❌ Signup error:', error);
      throw error;
    }
  }

  // Email/Password login
  async function login(email, password) {
    try {
      console.log('🔄 Attempting login for:', email);

      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json' 
        },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });

      // Handle different response types
      let data;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        // Handle non-JSON responses (like rate limiting)
        const text = await response.text();
        data = { message: text };
      }

      if (!response.ok) {
        // Handle rate limiting specifically
        if (response.status === 429) {
          throw new Error('Too many login attempts. Please wait a moment before trying again.');
        }
        throw new Error(data.message || 'Login failed');
      }

      console.log('✅ Login successful:', data.user.email);
      setCurrentUser(data.user);
      setPermissions(data.user.permissions || []);

      // Store JWT token if provided
      if (data.token) {
        localStorage.setItem('auth_token', data.token);
      }

      return data.user;
    } catch (error) {
      console.error('❌ Login error:', error);
      throw error;
    }
  }

  // Logout
  async function logout() {
    try {
      console.log('🔄 Logging out...');

      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });

      setCurrentUser(null);
      setPermissions([]);

      // Remove JWT token
      localStorage.removeItem('auth_token');

      console.log('✅ Logout successful');

      // Redirect to home page
      window.location.href = '/';
    } catch (error) {
      console.error('❌ Logout failed:', error);
      // Force logout on client side even if server request failed
      setCurrentUser(null);
      setPermissions([]);
      localStorage.removeItem('auth_token');
    }
  }

  // Check if user has specific permission
  function hasPermission(permission) {
    if (!currentUser) return false;
    if (currentUser.role === 'super_admin') return true;
    return permissions.includes(permission);
  }

  // Check if user has any of the specified roles
  function hasRole(roles) {
    if (!currentUser) return false;
    const userRoles = Array.isArray(currentUser.role) ? currentUser.role : [currentUser.role];
    const requiredRoles = Array.isArray(roles) ? roles : [roles];
    return requiredRoles.some(role => userRoles.includes(role));
  }

  // Check if user is admin
  function isAdmin() {
    return hasRole(['admin', 'super_admin']);
  }

  // Check if user is super admin
  function isSuperAdmin() {
    return hasRole(['super_admin']);
  }

  // Generate API token
  async function generateApiToken() {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/generate-token`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to generate API token');
      }

      return data.token;
    } catch (error) {
      console.error('❌ API token generation failed:', error);
      throw error;
    }
  }

  // Get user permissions info
  async function getUserPermissions() {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/permissions`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setPermissions(data.permissions || []);
        return data;
      }
      return null;
    } catch (error) {
      console.error('❌ Failed to get user permissions:', error);
      return null;
    }
  }

  // Refresh user data
  async function refreshUser() {
    await checkAuthStatus();
  }

  const value = {
    // User state
    currentUser,
    permissions,
    loading,

    // Authentication methods
    signup,
    login,
    loginWithGoogle,
    loginWithGitHub,
    logout,

    // Permission checks
    hasPermission,
    hasRole,
    isAdmin,
    isSuperAdmin,

    // Utility functions
    generateApiToken,
    getUserPermissions,
    refreshUser,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export default AuthContext;